import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type {
  CategoryEnum,
  MaterialUsageSummary,
  ProjectReportSummary,
  TechniciansReportSummary,
} from "../../app/generated-axios-client";

export type MaterialUsageReportFilters = {
  project?: string;
  location?: string;
  category?: CategoryEnum;
  dateFrom?: string;
  dateTo?: string;
  includeInProgress: boolean;
};

export type ProjectReportFilters = {
  project: string;
  dateFrom?: string;
  dateTo?: string;
};

export type TechniciansReportFilters = {
  // empty for every active technician
  technicians: number[];
  dateFrom?: string;
  dateTo?: string;
};

export type ReportRequestStatus = "idle" | "loading" | "succeeded" | "failed";

export interface ReportsState {
  materialUsageSummary: MaterialUsageSummary | null;
  // The filters the shown summary was built with, so the Excel download always
  // matches what is on screen even while a newer summary is still loading.
  materialUsageSummaryFilters: MaterialUsageReportFilters | null;
  materialUsageSummaryStatus: ReportRequestStatus;
  materialUsageDownloadStatus: ReportRequestStatus;
  projectReportSummary: ProjectReportSummary | null;
  // as with the material usage summary, the download uses the filters on screen
  projectReportSummaryFilters: ProjectReportFilters | null;
  projectReportSummaryStatus: ReportRequestStatus;
  projectReportDownloadStatus: ReportRequestStatus;
  techniciansReportSummary: TechniciansReportSummary | null;
  // as with the material usage summary, the download uses the filters on screen
  techniciansReportSummaryFilters: TechniciansReportFilters | null;
  techniciansReportSummaryStatus: ReportRequestStatus;
  techniciansReportDownloadStatus: ReportRequestStatus;
}

const initialState: ReportsState = {
  materialUsageSummary: null,
  materialUsageSummaryFilters: null,
  materialUsageSummaryStatus: "idle",
  materialUsageDownloadStatus: "idle",
  projectReportSummary: null,
  projectReportSummaryFilters: null,
  projectReportSummaryStatus: "idle",
  projectReportDownloadStatus: "idle",
  techniciansReportSummary: null,
  techniciansReportSummaryFilters: null,
  techniciansReportSummaryStatus: "idle",
  techniciansReportDownloadStatus: "idle",
};

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    fetchMaterialUsageSummary: (
      state,
      _action: PayloadAction<MaterialUsageReportFilters>
    ) => {
      state.materialUsageSummaryStatus = "loading";
    },
    storeMaterialUsageSummary: (
      state,
      action: PayloadAction<{
        filters: MaterialUsageReportFilters;
        summary: MaterialUsageSummary;
      }>
    ) => {
      state.materialUsageSummary = action.payload.summary;
      state.materialUsageSummaryFilters = action.payload.filters;
      state.materialUsageSummaryStatus = "succeeded";
    },
    setMaterialUsageSummaryFailed: (state) => {
      state.materialUsageSummary = null;
      state.materialUsageSummaryFilters = null;
      state.materialUsageSummaryStatus = "failed";
    },
    downloadMaterialUsageReport: (state) => {
      state.materialUsageDownloadStatus = "loading";
    },
    setMaterialUsageDownloadStatus: (
      state,
      action: PayloadAction<ReportRequestStatus>
    ) => {
      state.materialUsageDownloadStatus = action.payload;
    },
    fetchProjectReportSummary: (
      state,
      _action: PayloadAction<ProjectReportFilters>
    ) => {
      state.projectReportSummaryStatus = "loading";
    },
    storeProjectReportSummary: (
      state,
      action: PayloadAction<{
        filters: ProjectReportFilters;
        summary: ProjectReportSummary;
      }>
    ) => {
      state.projectReportSummary = action.payload.summary;
      state.projectReportSummaryFilters = action.payload.filters;
      state.projectReportSummaryStatus = "succeeded";
    },
    setProjectReportSummaryFailed: (state) => {
      state.projectReportSummary = null;
      state.projectReportSummaryFilters = null;
      state.projectReportSummaryStatus = "failed";
    },
    downloadProjectReport: (state) => {
      state.projectReportDownloadStatus = "loading";
    },
    setProjectReportDownloadStatus: (
      state,
      action: PayloadAction<ReportRequestStatus>
    ) => {
      state.projectReportDownloadStatus = action.payload;
    },
    fetchTechniciansReportSummary: (
      state,
      _action: PayloadAction<TechniciansReportFilters>
    ) => {
      state.techniciansReportSummaryStatus = "loading";
    },
    storeTechniciansReportSummary: (
      state,
      action: PayloadAction<{
        filters: TechniciansReportFilters;
        summary: TechniciansReportSummary;
      }>
    ) => {
      state.techniciansReportSummary = action.payload.summary;
      state.techniciansReportSummaryFilters = action.payload.filters;
      state.techniciansReportSummaryStatus = "succeeded";
    },
    setTechniciansReportSummaryFailed: (state) => {
      state.techniciansReportSummary = null;
      state.techniciansReportSummaryFilters = null;
      state.techniciansReportSummaryStatus = "failed";
    },
    downloadTechniciansReport: (state) => {
      state.techniciansReportDownloadStatus = "loading";
    },
    setTechniciansReportDownloadStatus: (
      state,
      action: PayloadAction<ReportRequestStatus>
    ) => {
      state.techniciansReportDownloadStatus = action.payload;
    },
    clearReports: () => initialState,
  },
});

export const reportsActions = reportsSlice.actions;
export default reportsSlice.reducer;
